/**
 * Coordenador de patch do History API para multi-instance.
 * Infraestrutura de browser — não armazena estado de negócio.
 */
type NavigationListener = () => void;

export class HistoryNavigationPatch {
  private static refCount = 0;

  private static readonly listeners = new Set<NavigationListener>();

  private static originalPushState: History['pushState'] | null = null;

  private static originalReplaceState: History['replaceState'] | null = null;

  private static installed = false;

  static subscribe(listener: NavigationListener): () => void {
    if (typeof window === 'undefined' || typeof history === 'undefined') {
      return () => {};
    }

    this.listeners.add(listener);
    this.refCount += 1;
    this.install();

    return () => {
      this.listeners.delete(listener);
      this.refCount -= 1;
      if (this.refCount <= 0) {
        this.uninstall();
        this.refCount = 0;
      }
    };
  }

  /** @internal Testes apenas. */
  static resetForTests(): void {
    this.listeners.clear();
    this.refCount = 0;
    this.uninstall();
  }

  private static install(): void {
    if (this.installed) return;

    this.originalPushState = history.pushState.bind(history);
    this.originalReplaceState = history.replaceState.bind(history);

    history.pushState = (...args: Parameters<History['pushState']>) => {
      this.originalPushState?.(...args);
      this.notify();
    };

    history.replaceState = (...args: Parameters<History['replaceState']>) => {
      this.originalReplaceState?.(...args);
      this.notify();
    };

    this.installed = true;
  }

  private static uninstall(): void {
    if (!this.installed) return;

    if (this.originalPushState) {
      history.pushState = this.originalPushState;
    }
    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState;
    }

    this.originalPushState = null;
    this.originalReplaceState = null;
    this.installed = false;
  }

  private static notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
